import React, { useEffect, useReducer } from "react";

import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

import { constants } from "./FeeAndAdjustmentResearchConstants";

import KendoExcelExporterDirect from "../../../components/export/KendoExcelExporterDirect";
import CsvExportDirect from "../../../components/export/CsvExportDirect";

import { useFetchExportDetailsData } from "./hooks/useFetchExportDetailsData";
import { useInsertServerSideExport } from "shared/hooks/useInsertServerSideExport";
import { useFetchDetailsApiArguments } from "./hooks/useFetchDetailsApiArguments";
import { ExceedsDirectExportRowLimit } from "shared/helpers/ExportRowCountHelpers";
import { LogError } from "components/AppInsights/TelemetryService";
import {
    GetSanitizedTelemetryError,
    GetSafeTelemetryProperties,
} from "shared/helpers/AxiosHelpers";

const GET_DETAILS_ENDPOINT = "api/FeeAndAdjustment/GetDetails";
const POST_DETAILS_TO_AZURE_STORAGE_ENDPOINT =
    "api/FeeAndAdjustment/PostGetDetailsToAzureStorage";

export default function FeeAndAdjustmentDirectExport(props) {
    let {
        email,
        submittedFormData,
        selectedFeeCodes,
        directExportIsProcessing,
        setDirectExportIsProcessing,
        directExportType,
        setIsDirectExportDataLoading,
        setIsDirectExportDataError,
        setIsDirectExportDataMessage,
    } = props;

    const {
        data: exportDetailsData,
        error: exportDetailsDataError,
        isLoading: isDirectExportDataLoading,
        isError: isDirectExportDataError,
    } = useFetchExportDetailsData(
        email,
        submittedFormData,
        selectedFeeCodes,
        directExportIsProcessing
    );

    let { mutate: mutateForServerSideExport } = useInsertServerSideExport();
    const { apiParams: exportRequestParameters } = useFetchDetailsApiArguments(
        email,
        submittedFormData,
        selectedFeeCodes
    );

    // Submits the export to the Report Queue (background processing) and reports the outcome to the parent.
    function queueServerSideExport(error, successDescription) {
        mutateForServerSideExport(
            {
                error,
                endpoint: POST_DETAILS_TO_AZURE_STORAGE_ENDPOINT,
                directParameters: exportRequestParameters,
            },
            {
                onSuccess: () => {
                    setIsDirectExportDataMessage({
                        isFailure: false,
                        description: successDescription,
                    });
                    setIsDirectExportDataError(true);
                },
                onError: () => {
                    // The fallback submission itself failed (e.g. PostGetDetailsToAzureStorage /
                    // InsertGetDetailsIntoAzureStorageTable threw or returned failure) — the fallback
                    // hook already logged the underlying error, so just surface a clear message.
                    setIsDirectExportDataMessage({
                        isFailure: true,
                        description: constants.exportMessages.submissionFailed,
                    });
                    setIsDirectExportDataError(true);
                },
            }
        );
    }

    useEffect(() => {
        if (isDirectExportDataError) {
            // The direct/client-side GetDetails request failed or timed out (sometimes surfacing
            // only as a generic IIS-style "resource has been moved" page with no usable HTTP
            // response). Log a sanitized error so App Insights has the root cause without the axios
            // request/response payloads, then always attempt the server-side fallback —
            // directParameters make this reliable even when the failure carried no response at all.
            LogError(GetSanitizedTelemetryError(exportDetailsDataError), {
                message: "Fee and Adjustment Export Details direct request failed",
                endpoint: GET_DETAILS_ENDPOINT,
                reportType: "FeesAndAdjustmentDetails",
                ...GetSafeTelemetryProperties(exportDetailsDataError),
            });

            queueServerSideExport(
                exportDetailsDataError,
                constants.exportMessages.queuedSuccess
            );

            setDirectExportIsProcessing(false);
        }

    }, [isDirectExportDataError]);

    function directExportReducer(directExportState, action) {
        let thisAction = action.type.toLowerCase();

        if (thisAction === "clear") {
            return null;
        }

        let thisExportData = null;
        if (action.payload.length > 0) {
            let exportColumns = [];
            let exportRows = [];
            let headerProps = constants.detailHeaderProps;

            let thisData = action.payload;

            Object.keys(thisData[0].details[0]).forEach((key) => {
                exportColumns.push({
                    title: headerProps[key].title,
                    type: headerProps[key].type,
                    accessor: key,
                    showTotal: headerProps[key].showTotal || false,
                    hidden: headerProps[key].hidden || false,
                });
            });

            thisData.forEach((item) => {
                item.details.forEach((detail) => {
                    exportRows.push(detail);
                });
            });

            //if no exportColumns or exportRows  return null
            if (!exportColumns || exportColumns.length === 0) return null;
            if (!exportRows || exportRows.length === 0) return null;
            exportColumns = exportColumns.filter((item) => item.hidden == false);
            thisExportData = {
                columns: exportColumns,
                rows: exportRows,
            };
        }

        return { ExportType: thisAction, ExportData: thisExportData };
    }

    const [directExportState, dispatchDirectExport] = useReducer(
        directExportReducer,
        null
    );

    //after excel report ios exported this function is called to set the export excel state back to false
    function DirectExportCompletedCallback() {
        dispatchDirectExport({ type: "clear" });
    }

    useEffect(() => {
        //Make sure to notofy the parent on the isDirectExportDataLoading loading state
        setIsDirectExportDataLoading(isDirectExportDataLoading);
        //Note: the error/message props are notified from the isDirectExportDataError effect above,
        //once the server-side export fallback has resolved (success or failure) — not here — so the
        //user is never shown the raw GetDetails failure before the fallback outcome is known.
        //if we are not processing the export, then return
        if (!directExportIsProcessing) return;

        // if the data is in loading state, then return
        if (isDirectExportDataLoading) return;

        //if there is an error, then return
        if (isDirectExportDataError) return;

        //make sure we have some data
        if (IsNullOrEmpty(exportDetailsData)) {
            alert("No data found for export");
        } else if (ExceedsDirectExportRowLimit(exportDetailsData)) {
            // Too many rows to build the Excel/CSV file in the browser without freezing the page,
            // so hand the export off to background (Report Queue) processing instead.
            queueServerSideExport(
                null,
                constants.exportMessages.largeDatasetQueued
            );
        } else {
            //dispatch the export
            dispatchDirectExport({
                type: directExportType,
                payload: exportDetailsData,
            });
        }

        //turn off the processing
        setDirectExportIsProcessing(false);

        //
    }, [directExportIsProcessing, isDirectExportDataLoading]);

    function GetProcessedExportDataColumns(sourceColumns) {
        let columns = [];

        if (!sourceColumns) return columns;

        if (sourceColumns.length > 0) {
            columns = sourceColumns.map((value) => {
                let newObject = {
                    field: value.accessor,
                    title: value.title,
                };

                return newObject;
            });
        }
        return columns;
    }

    return (
        <React.Fragment>
            {directExportState && directExportState.ExportType == "excel" && (
                <KendoExcelExporterDirect
                    data={directExportState.ExportData.rows}
                    columns={GetProcessedExportDataColumns(
                        directExportState.ExportData.columns
                    )}
                    exportCompletedCallback={DirectExportCompletedCallback}
                    exportFileNamePrefix={"Fee&AdjustmentDetails"}
                ></KendoExcelExporterDirect>
            )}
            {directExportState && directExportState.ExportType == "csv" && (
                <CsvExportDirect
                    columns={[]}
                    rows={[]}
                    custom={directExportState.ExportData}
                    exportCompletedCallback={DirectExportCompletedCallback}
                    exportFileNamePrefix={"Fee&AdjustmentDetails"}
                />
            )}
        </React.Fragment>
    );
}
